import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  Brain, RefreshCw, FileText, Users, Target, AlertTriangle,
  Link2, TrendingUp, Loader2, Sparkles, FileSearch, Camera, 
  Car, User, Image as ImageIcon, Upload
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const AIAssistant = () => {
  const token = localStorage.getItem('nexus_token');
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('analysis');
  
  // Analysis results
  const [caseAnalysis, setCaseAnalysis] = useState(null);
  const [suspectLinks, setSuspectLinks] = useState(null);
  const [behavioralAnalysis, setBehavioralAnalysis] = useState(null);
  const [generatedReport, setGeneratedReport] = useState(null);
  
  // Image recognition
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageAnalysisType, setImageAnalysisType] = useState('general');
  const [imageAnalysisResult, setImageAnalysisResult] = useState(null);
  
  // Report type
  const [reportType, setReportType] = useState('comprehensive');

  // Fetch cases
  const fetchCases = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setCases(await res.json());
      }
    } catch (e) {
      console.error('Fetch cases error:', e);
    }
  }, [token]);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  // Case Analysis
  const runCaseAnalysis = async () => {
    if (!selectedCase) {
      toast({ title: 'Select a Case', description: 'Please select a case to analyze', variant: 'destructive' });
      return;
    }
    setLoading(true);
    setCaseAnalysis(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${selectedCase}/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ analysis_type: 'comprehensive' })
      });
      if (res.ok) {
        const data = await res.json();
        setCaseAnalysis(data);
        toast({ title: 'Analysis Complete' });
      } else {
        throw new Error('Analysis failed');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Suspect Linking
  const runSuspectLinking = async () => {
    if (!selectedCase) {
      toast({ title: 'Select a Case', variant: 'destructive' });
      return;
    }
    setLoading(true);
    setSuspectLinks(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${selectedCase}/ai/link-suspects`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setSuspectLinks(await res.json());
        toast({ title: 'Linking Complete' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Behavioral Analysis
  const runBehavioralAnalysis = async () => {
    if (!selectedCase) {
      toast({ title: 'Select a Case', variant: 'destructive' });
      return;
    }
    setLoading(true);
    setBehavioralAnalysis(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${selectedCase}/ai/behavioral-analysis`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setBehavioralAnalysis(await res.json());
        toast({ title: 'Analysis Complete' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Generate Report
  const generateReport = async () => {
    if (!selectedCase) {
      toast({ title: 'Select a Case', variant: 'destructive' });
      return;
    }
    setLoading(true);
    setGeneratedReport(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${selectedCase}/ai/generate-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ report_type: reportType })
      });
      if (res.ok) {
        setGeneratedReport(await res.json());
        toast({ title: 'Report Generated' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Run image analysis
  const runImageAnalysis = async () => {
    if (!imageFile) {
      toast({ title: 'Select an Image', variant: 'destructive' });
      return;
    }
    setLoading(true);
    setImageAnalysisResult(null);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.readAsDataURL(imageFile);
      reader.onloadend = async () => {
        const base64 = reader.result?.toString().split(',')[1];
        
        let endpoint = '/api/ai/image/analyze';
        let body = { image_base64: base64, analysis_type: imageAnalysisType };
        
        if (imageAnalysisType === 'facial') {
          endpoint = '/api/ai/image/facial-analysis';
          body = { image_base64: base64 };
        } else if (imageAnalysisType === 'license_plate') {
          endpoint = '/api/ai/image/license-plate';
          body = { image_base64: base64 };
        }
        
        const res = await fetch(`${BACKEND_URL}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body)
        });
        
        if (res.ok) {
          setImageAnalysisResult(await res.json());
          toast({ title: 'Image Analyzed' });
        } else {
          throw new Error('Analysis failed');
        }
        setLoading(false);
      };
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
      setLoading(false);
    }
  };

  const selectedCaseData = cases.find(c => c.id === selectedCase);

  return (
    <div className="space-y-6" data-testid="ai-assistant-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#F8FAFC] flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#8B5CF6] to-[#6366F1] rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            AI Investigation Assistant
          </h1>
          <p className="text-[#94A3B8] mt-1">GPT-5.2 powered analysis and image recognition</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedCase} onValueChange={setSelectedCase}>
            <SelectTrigger className="w-[280px] bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="case-select">
              <SelectValue placeholder="Select case for analysis" />
            </SelectTrigger>
            <SelectContent className="bg-[#131A2A] border-[#334155] max-h-[300px]">
              {cases.map(c => (
                <SelectItem key={c.id} value={c.id} className="text-[#F8FAFC]">
                  {c.case_number} - {c.title?.substring(0, 30)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Case Info */}
      {selectedCaseData && (
        <Card className="bg-[#131A2A] border-[#334155]">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <Badge className="bg-[#8B5CF6]/20 text-[#8B5CF6] border border-[#8B5CF6]/30 px-3">
                  {selectedCaseData.case_number}
                </Badge>
                <span className="text-[#F8FAFC] font-medium">{selectedCaseData.title}</span>
              </div>
              <Badge className="bg-[#182136] text-[#94A3B8] border border-[#334155]">
                {selectedCaseData.case_type?.replace(/_/g, ' ')}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#0B0F19] border border-[#334155] p-1">
          <TabsTrigger value="analysis" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8]">
            <Target className="w-4 h-4 mr-2" /> Case Analysis
          </TabsTrigger>
          <TabsTrigger value="linking" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8]">
            <Link2 className="w-4 h-4 mr-2" /> Suspect Linking
          </TabsTrigger>
          <TabsTrigger value="behavioral" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8]">
            <TrendingUp className="w-4 h-4 mr-2" /> Behavioral Patterns
          </TabsTrigger>
          <TabsTrigger value="reports" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8]">
            <FileText className="w-4 h-4 mr-2" /> Report Generator
          </TabsTrigger>
          <TabsTrigger value="recognition" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8]">
            <Camera className="w-4 h-4 mr-2" /> Image Recognition
          </TabsTrigger>
        </TabsList>

        {/* Case Analysis Tab */}
        <TabsContent value="analysis" className="space-y-4">
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader>
              <CardTitle className="text-[#F8FAFC] flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#8B5CF6]" />
                Comprehensive Case Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] text-sm mb-4">
                AI will analyze all case data including subjects, vehicles, leads, notes, and evidence to provide insights, risk assessment, and recommended actions.
              </p>
              <Button 
                className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
                onClick={runCaseAnalysis}
                disabled={loading || !selectedCase}
                data-testid="run-analysis-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                Run Analysis
              </Button>
            </CardContent>
          </Card>

          {caseAnalysis && (
            <Card className="bg-[#0B0F19] border-[#334155]">
              <CardHeader className="border-b border-[#334155]">
                <CardTitle className="text-[#F8FAFC] text-sm flex items-center justify-between">
                  <span>Analysis Results</span>
                  <Badge className="bg-[#10B981]/20 text-[#10B981]">Generated {new Date(caseAnalysis.generated_at).toLocaleString()}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="prose prose-invert prose-sm max-w-none text-[#E2E8F0]">
                  <ReactMarkdown>{caseAnalysis.analysis}</ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Suspect Linking Tab */}
        <TabsContent value="linking" className="space-y-4">
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader>
              <CardTitle className="text-[#F8FAFC] flex items-center gap-2">
                <Link2 className="w-5 h-5 text-[#F59E0B]" />
                Suspect Linking Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] text-sm mb-4">
                Identify connections between subjects based on shared addresses, phone numbers, associates, vehicles, and behavioral patterns.
              </p>
              <Button 
                className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
                onClick={runSuspectLinking}
                disabled={loading || !selectedCase}
                data-testid="run-linking-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                Analyze Links
              </Button>
            </CardContent>
          </Card>

          {suspectLinks && (
            <Card className="bg-[#0B0F19] border-[#334155]">
              <CardHeader className="border-b border-[#334155]">
                <CardTitle className="text-[#F8FAFC] text-sm">
                  Link Analysis ({suspectLinks.subjects_analyzed} subjects analyzed)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="prose prose-invert prose-sm max-w-none text-[#E2E8F0]">
                  <ReactMarkdown>{suspectLinks.analysis}</ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Behavioral Tab */}
        <TabsContent value="behavioral" className="space-y-4">
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader>
              <CardTitle className="text-[#F8FAFC] flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#10B981]" />
                Behavioral Pattern Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] text-sm mb-4">
                Analyze investigation notes and timeline to identify daily routines, weekly patterns, anomalies, and predictive insights.
              </p>
              <Button 
                className="bg-[#10B981] hover:bg-[#059669] text-white"
                onClick={runBehavioralAnalysis}
                disabled={loading || !selectedCase}
                data-testid="run-behavioral-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
                Analyze Patterns
              </Button>
            </CardContent>
          </Card>

          {behavioralAnalysis && (
            <Card className="bg-[#0B0F19] border-[#334155]">
              <CardHeader className="border-b border-[#334155]">
                <CardTitle className="text-[#F8FAFC] text-sm">Behavioral Analysis Results</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="prose prose-invert prose-sm max-w-none text-[#E2E8F0]">
                  <ReactMarkdown>{behavioralAnalysis.analysis}</ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader>
              <CardTitle className="text-[#F8FAFC] flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#3B82F6]" />
                AI Report Generator
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] text-sm mb-4">
                Generate professional investigation reports suitable for clients or legal proceedings.
              </p>
              <div className="flex items-center gap-4 mb-4">
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger className="w-[200px] bg-[#0B0F19] border-[#334155] text-[#F8FAFC]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#131A2A] border-[#334155]">
                    <SelectItem value="comprehensive" className="text-[#F8FAFC]">Comprehensive</SelectItem>
                    <SelectItem value="summary" className="text-[#F8FAFC]">Executive Summary</SelectItem>
                    <SelectItem value="surveillance" className="text-[#F8FAFC]">Surveillance Report</SelectItem>
                    <SelectItem value="skip_trace" className="text-[#F8FAFC]">Skip Trace Report</SelectItem>
                    <SelectItem value="background" className="text-[#F8FAFC]">Background Report</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
                  onClick={generateReport}
                  disabled={loading || !selectedCase}
                  data-testid="generate-report-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSearch className="w-4 h-4 mr-2" />}
                  Generate Report
                </Button>
              </div>
            </CardContent>
          </Card>

          {generatedReport && (
            <Card className="bg-[#0B0F19] border-[#334155]">
              <CardHeader className="border-b border-[#334155]">
                <CardTitle className="text-[#F8FAFC] text-sm flex items-center justify-between">
                  <span>{reportType.replace('_', ' ').toUpperCase()} - {generatedReport.case_number}</span>
                  <Badge className="bg-[#3B82F6]/20 text-[#3B82F6]">{new Date(generatedReport.generated_at).toLocaleString()}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="prose prose-invert prose-sm max-w-none text-[#E2E8F0]">
                  <ReactMarkdown>{generatedReport.report}</ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Image Recognition Tab */}
        <TabsContent value="recognition" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-[#131A2A] border-[#334155]">
              <CardHeader>
                <CardTitle className="text-[#F8FAFC] flex items-center gap-2">
                  <Camera className="w-5 h-5 text-[#DC2626]" />
                  Image Recognition
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border-2 border-dashed border-[#334155] rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" className="cursor-pointer">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
                    ) : (
                      <div className="py-8">
                        <Upload className="w-12 h-12 mx-auto text-[#94A3B8] mb-3" />
                        <p className="text-[#94A3B8]">Click to upload image</p>
                        <p className="text-xs text-[#64748B] mt-1">JPG, PNG, WEBP supported</p>
                      </div>
                    )}
                  </label>
                </div>

                <Select value={imageAnalysisType} onValueChange={setImageAnalysisType}>
                  <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#131A2A] border-[#334155]">
                    <SelectItem value="general" className="text-[#F8FAFC]">
                      <div className="flex items-center gap-2">
                        <ImageIcon className="w-4 h-4" /> General Analysis
                      </div>
                    </SelectItem>
                    <SelectItem value="facial" className="text-[#F8FAFC]">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" /> Facial Analysis
                      </div>
                    </SelectItem>
                    <SelectItem value="license_plate" className="text-[#F8FAFC]">
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4" /> License Plate
                      </div>
                    </SelectItem>
                    <SelectItem value="vehicle" className="text-[#F8FAFC]">
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4" /> Vehicle Analysis
                      </div>
                    </SelectItem>
                    <SelectItem value="location" className="text-[#F8FAFC]">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4" /> Location Clues
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>

                <Button 
                  className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                  onClick={runImageAnalysis}
                  disabled={loading || !imageFile}
                  data-testid="analyze-image-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
                  Analyze Image
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-[#0B0F19] border-[#334155]">
              <CardHeader className="border-b border-[#334155]">
                <CardTitle className="text-[#F8FAFC] text-sm">Analysis Results</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {imageAnalysisResult ? (
                  <div className="space-y-4">
                    {imageAnalysisResult.plate_number && (
                      <div className="bg-[#131A2A] p-4 rounded-lg border border-[#334155]">
                        <p className="text-xs text-[#94A3B8] mb-1">Detected Plate</p>
                        <p className="text-2xl font-mono font-bold text-[#F8FAFC]">{imageAnalysisResult.plate_number}</p>
                        {imageAnalysisResult.state && <p className="text-sm text-[#94A3B8]">State: {imageAnalysisResult.state}</p>}
                        {imageAnalysisResult.confidence && <Badge className="mt-2 bg-[#10B981]/20 text-[#10B981]">{imageAnalysisResult.confidence} Confidence</Badge>}
                      </div>
                    )}
                    {imageAnalysisResult.faces_detected !== undefined && imageAnalysisResult.faces_detected >= 0 && (
                      <div className="bg-[#131A2A] p-4 rounded-lg border border-[#334155]">
                        <p className="text-xs text-[#94A3B8] mb-1">Faces Detected</p>
                        <p className="text-2xl font-bold text-[#F8FAFC]">{imageAnalysisResult.faces_detected}</p>
                      </div>
                    )}
                    <div className="prose prose-invert prose-sm max-w-none text-[#E2E8F0]">
                      <ReactMarkdown>{imageAnalysisResult.analysis || imageAnalysisResult.raw_analysis}</ReactMarkdown>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-[#94A3B8]">
                    <Camera className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Upload an image and run analysis to see results</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIAssistant;
